import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, CardBody, CardHeader } from '@heroui/react';
import React, { useEffect, useRef, useState } from 'react';
import { OrderBoardColumn as OrderBoardColumnType } from '../../types/orderBoard';
import BoardCard from './BoardCard';

interface BoardColumnProps {
  column: OrderBoardColumnType;
  onCardClick?: (cardId: number) => void;
  hideCardIds?: Set<number>;
  isLocked?: boolean;
}

const BoardColumn: React.FC<BoardColumnProps> = ({
  column,
  onCardClick,
  hideCardIds,
  isLocked = false,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.id}`, // Prefix to ensure uniqueness and avoid conflicts
    data: {
      type: 'column',
      columnId: column.id,
    },
  });

  const cardIds = column.cards.map(card => card.id.toString());

  // Track scrolling state for auto-hiding scrollbars
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cardBodyRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  useEffect(() => {
    const cardBody = cardBodyRef.current;
    if (cardBody) {
      cardBody.addEventListener('scroll', handleScroll);
      return () => {
        cardBody.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, []);

  return (
    <Card
      ref={setNodeRef}
      className={`h-full flex flex-col min-w-[240px] md:min-w-[280px] max-h-full flex-shrink-0 ${
        isOver ? 'ring-2 ring-primary-500 bg-primary-50' : ''
      }`}
    >
      <CardHeader className='pb-2 flex-shrink-0 px-3 md:px-4'>
        <div className='flex items-center justify-between w-full'>
          <h3 className='font-semibold text-xs md:text-sm text-gray-900 truncate flex-1'>
            {column.name}
          </h3>
          <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full ml-2 flex-shrink-0'>
            {column.cards.length}
          </span>
        </div>
      </CardHeader>
      <CardBody className='flex-1 overflow-hidden p-0 min-h-0'>
        <div
          ref={cardBodyRef}
          className={`h-full overflow-y-auto p-2 md:p-3 pb-4 md:pb-6 space-y-2 touch-pan-y scrollbar-auto-hide ${
            isScrolling ? 'scrolling' : ''
          }`}
        >
          <SortableContext
            items={cardIds}
            strategy={verticalListSortingStrategy}
          >
            {column.cards.map(card => {
              // Hide card if it's being moved (will appear in new column)
              if (hideCardIds?.has(card.id)) {
                return null;
              }
              return (
                <BoardCard
                  key={`${card.id}-${card.columnId}`}
                  card={card}
                  onClick={() => onCardClick?.(card.id)}
                  isLocked={isLocked}
                />
              );
            })}
          </SortableContext>
        </div>
      </CardBody>
    </Card>
  );
};

export default BoardColumn;
